/** Resumen: lo que se despachó y lo que hay que atender. */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FilePlus2, TriangleAlert } from 'lucide-react';
import { Alerta, Boton, Chip, Indicador, Tarjeta, TarjetaTitulo, Vacio } from '@/components/ui';
import {
  ComparativaMensual,
  DespachoDiario,
  DispersionViajes,
  PorCliente,
  PorDestino,
  PorPlanta,
  PorProducto,
  PorTurno,
} from './graficos';
import { useCatalogos } from '@/hooks/useCatalogos';
import { aFechaISO, fecha as ffecha, m3 as fm3, uf, clp } from '@/domain/formato';
import { controlarContrato } from '@/domain/precios';
import type { DocGuia } from '@/db/tiposDatos';
import { useGuias } from '@/hooks/useGuias';
import { leerContrato, type DocContrato } from '@/db/contrato';

export default function Resumen() {
  const { catalogos } = useCatalogos();
  const { guias, cargando } = useGuias();
  // Arranca en el año: un mes suelto da una foto demasiado corta para leer
  // tendencias, y desde ahí se baja a un mes o se abre a todo el contrato.
  const [periodo, setPeriodo] = useState<'mes' | 'anio' | 'todo'>('anio');

  // El contrato es un dato administrado, no una constante: si no está
  // configurado, esta pantalla no inventa un semáforo.
  const [contratoDoc, setContratoDoc] = useState<DocContrato | null>(null);
  const [buscandoContrato, setBuscandoContrato] = useState(true);

  useEffect(() => {
    void leerContrato()
      .then(setContratoDoc)
      .finally(() => setBuscandoContrato(false));
  }, []);

  const hoy = aFechaISO(new Date());

  /**
   * Fecha de la guía más reciente. Con un libro histórico recién importado
   * puede ser de hace semanas, y entonces los indicadores del día y del mes
   * dan cero aunque haya miles de guías: conviene decirlo, no dejar los ceros
   * solos pareciendo una falla.
   */
  const ultimaFecha = guias[0]?.fecha ?? null;
  const hayRecientes = Boolean(ultimaFecha && ultimaFecha >= hoy.slice(0, 8) + '01');
  const inicioMes = aFechaISO(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

  const vivas = useMemo(
    () => guias.filter((g) => g.estado !== 'ANULADA' && !g.eliminadoEn),
    [guias],
  );

  const k = useMemo(() => {
    const delDia = vivas.filter((g) => g.fecha === hoy);
    const delMes = vivas.filter((g) => g.fecha >= inicioMes);
    const suma = (xs: DocGuia[]) => xs.reduce((s, g) => s + (g.cantidadM3 || 0), 0);
    return {
      m3Hoy: suma(delDia),
      viajesHoy: delDia.length,
      m3Mes: suma(delMes),
      viajesMes: delMes.length,
      bloquesMes: delMes.reduce((s, g) => s + (g.bloquesCodelco ?? 0), 0),
      incompletas: vivas.filter((g) => !g.tiemposCompletos).length,
      borradores: vivas.filter((g) => g.estado === 'BORRADOR').length,
      porClasificar: vivas.filter((g) => g.estado === 'REQUIERE_CLASIFICACION').length,
      sinPartida: vivas.filter((g) => !g.itemContratoCodigo).length,
    };
  }, [vivas, hoy, inicioMes]);

  /** Guías del período elegido, para los gráficos de composición. */
  const delPeriodo = useMemo(() => {
    if (periodo === 'todo') return vivas;
    const inicio =
      periodo === 'mes' ? inicioMes : aFechaISO(new Date(new Date().getFullYear(), 0, 1));
    return vivas.filter((g) => g.fecha >= inicio);
  }, [vivas, periodo, inicioMes]);

  /**
   * Si el año corriente tampoco tiene guías —libro histórico que termina en un
   * año anterior— se abre en todo el contrato en vez de mostrar gráficos en
   * blanco. Una sola vez: después manda lo que elija el usuario.
   */
  const ajustado = useRef(false);
  useEffect(() => {
    if (ajustado.current || cargando || vivas.length === 0) return;
    ajustado.current = true;
    const inicioAnio = aFechaISO(new Date(new Date().getFullYear(), 0, 1));
    if (!vivas.some((g) => g.fecha >= inicioAnio)) setPeriodo('todo');
  }, [cargando, vivas]);

  const contrato = contratoDoc ? controlarContrato(contratoDoc) : null;

  const bandejas = [
    {
      n: k.borradores,
      texto: 'guías en borrador',
      a: '/guias?estado=BORRADOR',
    },
    { n: k.porClasificar, texto: 'productos por clasificar', a: '/guias' },
    { n: k.sinPartida, texto: 'guías sin partida asignada', a: '/guias' },
    { n: k.incompletas, texto: 'guías sin los 6 horarios', a: '/guias' },
  ].filter((b) => b.n > 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Resumen</h1>
          <p className="text-xs text-texto-mute">{ffecha(hoy)}</p>
        </div>
        <Link to="/guias/nueva">
          <Boton variante="primario" icono={<FilePlus2 size={15} />}>
            Nueva guía
          </Boton>
        </Link>
      </div>

      {/* Semáforo de contrato — el número que el Excel calculaba y nadie miraba.
          El acumulado cobrado se ingresa en Administración: viene de los
          estados de pago anteriores a la app, no de las guías cargadas. */}
      {contrato ? (
        <Alerta nivel={contrato.nivel} titulo={contrato.mensaje}>
          Contratado UF {uf(contrato.montoVigenteTotalUf, 0)} · cobrado UF{' '}
          {uf(contrato.cobradoAcumuladoUf, 0)} · saldo UF {uf(contrato.saldoUf, 0)}
          {contrato.excedido && ` · exceso ${clp(contrato.excesoClp)}`}
          <span className="mt-1 block opacity-70">
            El cobrado acumulado es un dato ingresado en Administración, de los estados de pago
            anteriores a la app. No se calcula con las guías cargadas.
          </span>
        </Alerta>
      ) : buscandoContrato ? null : (
        <Alerta nivel="NEUTRO" titulo="Contrato sin configurar">
          Falta cargar los montos del contrato para poder avisar cuánto se lleva consumido. Se
          configuran en Administración.
        </Alerta>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Indicador
          etiqueta="Despachado hoy"
          valor={fm3(k.m3Hoy)}
          unidad="m³"
          detalle={`${k.viajesHoy} viaje${k.viajesHoy === 1 ? '' : 's'}`}
        />
        <Indicador
          etiqueta="Despachado del mes"
          valor={fm3(k.m3Mes)}
          unidad="m³"
          detalle={`${k.viajesMes} viajes`}
        />
        <Indicador
          etiqueta="Sobrestadía del mes"
          valor={k.bloquesMes}
          unidad="bloques"
          nivel={k.bloquesMes > 0 ? 'AMARILLO' : 'NEUTRO'}
          detalle="de 10 min, regla Codelco"
        />
        <Indicador
          etiqueta="Guías sin horarios"
          valor={k.incompletas}
          nivel={k.incompletas > 0 ? 'ROJO' : 'VERDE'}
          detalle={k.incompletas > 0 ? 'no se les puede cobrar espera' : 'todas completas'}
        />
      </div>

      {bandejas.length > 0 && (
        <Tarjeta>
          <TarjetaTitulo>Pendientes</TarjetaTitulo>
          <div className="flex flex-wrap gap-2 p-4">
            {bandejas.map((b) => (
              <Link key={b.texto} to={b.a}>
                <Chip nivel="AMARILLO">
                  <TriangleAlert size={12} /> {b.n} {b.texto}
                </Chip>
              </Link>
            ))}
          </div>
        </Tarjeta>
      )}

      {cargando ? (
        <Tarjeta>
          <Vacio titulo="Cargando…" />
        </Tarjeta>
      ) : vivas.length === 0 ? (
        <Tarjeta>
          <TarjetaTitulo>Despacho de los últimos 14 días</TarjetaTitulo>
          <Vacio titulo="Todavía no hay guías cargadas">
            Carga la primera desde «Nueva guía», o prueba con datos de ejemplo desde Administración.
          </Vacio>
        </Tarjeta>
      ) : (
        <DespachoDiario guias={vivas} hoy={hoy} hasta={ultimaFecha ?? hoy} />
      )}

      {!hayRecientes && ultimaFecha && (
        <p className="text-xs text-texto-mute">
          Los indicadores de hoy y del mes están en cero porque la guía más reciente cargada es del{' '}
          <strong className="text-texto">{ffecha(ultimaFecha)}</strong>. Hay{' '}
          {guias.length.toLocaleString('es-CL')} guías en el sistema; para verlas, usa el Buscador o
          ajusta el rango en Guías.
        </p>
      )}

      {/* Composición del despacho */}
      <div className="flex items-center justify-between gap-3 pt-1">
        <h2 className="text-sm font-semibold">Composición del despacho</h2>
        <div className="flex gap-1 rounded-control bg-petroleo-800/60 p-1">
          {(
            [
              ['mes', 'Este mes'],
              ['anio', 'Este año'],
              ['todo', 'Todo'],
            ] as const
          ).map(([id, texto]) => (
            <button
              key={id}
              type="button"
              onClick={() => setPeriodo(id)}
              className={
                'rounded-control px-3 py-1.5 text-xs font-medium transition-colors ' +
                (periodo === id ? 'bg-petroleo-500 text-texto' : 'text-texto-mute hover:text-texto')
              }
            >
              {texto}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <PorPlanta guias={delPeriodo} />
        <PorTurno guias={delPeriodo} />
        <PorCliente guias={delPeriodo} />
        <PorProducto guias={delPeriodo} />
        <PorDestino guias={delPeriodo} />
        <ComparativaMensual guias={vivas} hasta={ultimaFecha ?? hoy} />
        <DispersionViajes guias={delPeriodo} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Tarjeta>
          <TarjetaTitulo>Últimas guías</TarjetaTitulo>
          {vivas.length === 0 ? (
            <Vacio titulo="Sin movimiento" />
          ) : (
            <div className="divide-y divide-linea/60">
              {vivas.slice(0, 6).map((g) => (
                <Link
                  key={g.id}
                  to={`/guias/${g.id}`}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/[.03]"
                >
                  <span className="font-mono text-xs tabular-nums text-texto-mute">#{g.folio}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">{g.clienteNombre}</div>
                    <div className="truncate font-mono text-[11px] text-texto-mute">
                      {g.productoCodigo}
                    </div>
                  </div>
                  <span className="shrink-0 font-mono text-sm tabular-nums">
                    {fm3(g.cantidadM3)} m³
                  </span>
                </Link>
              ))}
            </div>
          )}
        </Tarjeta>

        <Tarjeta>
          <TarjetaTitulo>Catálogos cargados</TarjetaTitulo>
          <div className="grid grid-cols-2 gap-2 p-4 text-sm">
            {[
              ['Partidas del contrato', catalogos.items.length],
              ['Productos', catalogos.productos.length],
              ['Clientes', catalogos.clientes.length],
              ['Destinos', catalogos.destinos.length],
              ['Equipos', catalogos.equipos.length],
              ['Centros de costo', catalogos.centrosCosto.length],
            ].map(([t, n]) => (
              <div key={t as string} className="rounded-control bg-petroleo-800/40 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-texto-mute">{t}</div>
                <div className="font-mono text-base tabular-nums">{n as number}</div>
              </div>
            ))}
          </div>
        </Tarjeta>
      </div>
    </div>
  );
}
